/**
 * Mirrors a region north-south (the แคว้นเก้ากระบี่, docs/archive/OPEN_WORLD.md "Round 2"): every y becomes `size - y`, so the sea
 * that lay north of the Yanyu region lies south, as in the new Yanyu (data/layouts/yanyu2.ts). Pure data transforms; the
 * compiler (`sketch.ts`) knows `flipped` for the coast and the range. Rivers keep their point order (their beds still
 * fall toward the sea), roads and shapes keep theirs.
 */
import type { Facing } from '../map';
import type { Side } from '../data/zones';
import type { BlockoutBox, RegionSketch, RegionWild, XY } from './sketch';

const SIDE: Readonly<Record<Side, Side>> = { n: 's', s: 'n', e: 'e', w: 'w' };
const FACE: Readonly<Record<Facing, Facing>> = { n: 's', s: 'n', e: 'e', w: 'w' };

/** The mirror of a sketch of side `size`, with a new id and core. */
export function flipSketch(
  src: RegionSketch,
  opts: { id: string; core: RegionSketch['core']; drop?: (x: number, y: number) => boolean },
): RegionSketch {
  const S = src.size;
  const fy = (y: number): number => S - y;
  const pt = ([x, y]: XY): XY => [x, fy(y)];
  const pts = (a: readonly XY[]): XY[] => a.map(pt);
  const rect = ([x0, x1, y0, y1]: RegionWild['rect']): RegionWild['rect'] => [x0, x1, fy(y1), fy(y0)];
  const [bx, by, bw, bh] = src.spires.box;
  return {
    ...src,
    id: opts.id,
    flipped: true,
    core: opts.core,
    coast: pts(src.coast),
    islands: src.islands.map((i) => ({ ...i, y: fy(i.y) })),
    stoneLegs: src.stoneLegs.map(([a, b]) => [pt(a), pt(b)] as const),
    peaks: src.peaks.map((p) => ({ ...p, y: fy(p.y) })),
    southRidge: { ...src.southRidge, pts: pts(src.southRidge.pts), edgeFrom: fy(src.southRidge.edgeFrom) },
    passes: src.passes.map((p) => ({ ...p, y: fy(p.y) })),
    flats: src.flats.map((f) => ({ ...f, y: fy(f.y) })),
    rivers: src.rivers.map((r) => ({ ...r, pts: pts(r.pts) })),
    falls: src.falls.map((f) => ({ ...f, y: fy(f.y) })),
    lakes: src.lakes.map((l) => ({ ...l, y: fy(l.y) })),
    cover: src.cover.map(([c, x, y, r]) => [c, x, fy(y), r] as const),
    roads: src.roads.map((r) => ({ ...r, pts: pts(r.pts) })),
    grounds: src.grounds.filter((g) => !opts.drop?.(g.x, fy(g.y))).map((g) => ({ ...g, y: fy(g.y) })),
    spires: {
      ...src.spires,
      box: [bx, fy(by + bh), bw, bh],
      ...(src.spires.avoid ? { avoid: src.spires.avoid.map(([x, y, r]) => [x, fy(y), r] as const) } : {}),
      extra: src.spires.extra.map((e) => ({ ...e, y: fy(e.y) })),
    },
    ...(src.outline ? { outline: pts(src.outline) } : {}),
    stations: src.stations.map((s) => ({ ...s, y: fy(s.y) })),
    portals: src.portals.map((p) => ({ ...p, y: fy(p.y), side: SIDE[p.side] })),
    ...(src.wilds ? { wilds: src.wilds.map((w) => ({ ...w, rect: rect(w.rect) })) } : {}),
  };
}

/** The mirror of the boxes of a region of side `size`; boxes for which `drop(x, y)` (mirrored) holds are left out. */
export function flipBlockout(
  boxes: readonly BlockoutBox[],
  size: number,
  drop?: (x: number, y: number) => boolean,
): BlockoutBox[] {
  return boxes
    .map((b) => ({ ...b, y: size - b.y, ...(b.down ? { down: FACE[b.down] } : {}) }))
    .filter((b) => !drop?.(b.x, b.y));
}
